import React, { useMemo, useState } from "react";
import { Button, Tag, Space, Typography, Empty } from "antd";
import { PlusOutlined, CloseOutlined, SwapOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { createOrderActions } from "../../../../store/slices/create-order-slice/create.orders.slice";
import { DishType } from "../../../../constants/enums";
import { groupCustomDishes } from "../../../../helpers/groupCustomDishes";
import AddCustomDishModal from "./AddCustomDishModal";

const { Text } = Typography;

const DISH_TYPE_COLORS = {
  [DishType.BASE]: "blue",
  [DishType.PROTEIN]: "orange",
  [DishType.SAUCE]: "green",
  [DishType.REGULAR]: "default",
};

function CustomMenuBuilder() {
  const dispatch = useDispatch();
  const { i18n, t } = useTranslation();
  const currentLanguage = i18n.language;

  const { selectedDates, customDishes } = useSelector((store) => store.createOrder);

  const [addDishModal, setAddDishModal] = useState({ visible: false, date: null, typeFilter: null, insertAtIndex: null, isMainDish: false });

  const getDishName = (dish) =>
    dish?.name?.[currentLanguage] || dish?.name?.en || "-";

  const getNutritionalSummaryForDate = (date) => {
    const dateStr = dayjs(date).format("YYYY-MM-DD");
    const dishesForDate = customDishes[dateStr] || [];

    if (dishesForDate.length === 0) {
      return { totalEnergy: 0, totalProteins: 0, totalFats: 0, totalCarbs: 0, totalWeight: 0, proteinPercentage: 0, fatPercentage: 0, carbPercentage: 0 };
    }

    const totals = dishesForDate.reduce(
      (acc, dish) => ({
        totalEnergy: acc.totalEnergy + (Number(dish.energy) || 0),
        totalProteins: acc.totalProteins + (Number(dish.proteins) || 0),
        totalFats: acc.totalFats + (Number(dish.fats) || 0),
        totalCarbs: acc.totalCarbs + (Number(dish.carbohydrates) || Number(dish.carbs) || 0),
        totalWeight: acc.totalWeight + (Number(dish.weightNetto) || Number(dish.weight) || 0),
      }),
      { totalEnergy: 0, totalProteins: 0, totalFats: 0, totalCarbs: 0, totalWeight: 0 }
    );

    const pCal = totals.totalProteins * 4;
    const fCal = totals.totalFats * 9;
    const cCal = totals.totalCarbs * 4;
    const totalMacroCal = pCal + fCal + cCal;

    return {
      ...totals,
      proteinPercentage: totalMacroCal > 0 ? (pCal / totalMacroCal) * 100 : 0,
      fatPercentage: totalMacroCal > 0 ? (fCal / totalMacroCal) * 100 : 0,
      carbPercentage: totalMacroCal > 0 ? (cCal / totalMacroCal) * 100 : 0,
    };
  };

  const handleRemoveGroup = (dateStr, group) => {
    const indices = [group.main.index, ...group.subDishes.map((s) => s.index)];
    dispatch(createOrderActions.removeCustomDishGroup({ date: dateStr, indices }));
  };

  const handleRemoveSubDish = (dateStr, index) => {
    dispatch(createOrderActions.removeCustomDish({ date: dateStr, index }));
  };

  const handleAddSubDish = (dateStr, group, dishType) => {
    const insertAtIndex = group.main.index + group.subDishes.length + 1;
    setAddDishModal({ visible: true, date: dateStr, typeFilter: dishType, insertAtIndex });
  };

  const handleChangeSubDish = (dateStr, subIndex, group, dishType) => {
    dispatch(createOrderActions.removeCustomDish({ date: dateStr, index: subIndex }));
    const remainingSubCount = group.subDishes.filter((s) => s.index !== subIndex).length;
    const insertAtIndex = group.main.index + remainingSubCount + 1;
    setAddDishModal({ visible: true, date: dateStr, typeFilter: dishType, insertAtIndex });
  };

  const MacronutrientPieChart = ({ proteinPercentage, fatPercentage, carbPercentage }) => {
    const size = 40;
    const strokeWidth = 4;
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;

    const proteinDash = (proteinPercentage / 100) * circumference;
    const fatDash = (fatPercentage / 100) * circumference;
    const carbDash = (carbPercentage / 100) * circumference;

    return (
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="transparent" stroke="#e0e0e0" strokeWidth={strokeWidth} />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="transparent" stroke="#3498db" strokeWidth={strokeWidth} strokeDasharray={`${proteinDash} ${circumference}`} strokeDashoffset={0} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="transparent" stroke="#f39c12" strokeWidth={strokeWidth} strokeDasharray={`${fatDash} ${circumference}`} strokeDashoffset={-proteinDash} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="transparent" stroke="#27ae60" strokeWidth={strokeWidth} strokeDasharray={`${carbDash} ${circumference}`} strokeDashoffset={-(proteinDash + fatDash)} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      </svg>
    );
  };

  return (
    <>
      {selectedDates.map((date) => {
        const dateStr = dayjs(date).format("YYYY-MM-DD");
        const dishesForDate = customDishes[dateStr] || [];
        const groups = groupCustomDishes(dishesForDate);
        const nutrition = getNutritionalSummaryForDate(date);

        return (
          <div key={dateStr} style={{ marginBottom: 24 }}>
            <h3>{dayjs(date).format("DD.MM.YYYY")}</h3>

            {/* Nutritional summary */}
            <div style={{ marginBottom: 16, background: "#fafbfc", borderRadius: 8, padding: 16, boxShadow: "0 1px 4px 0 #e3e6e8" }}>
              <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>
                {t("Daily Total")}
              </div>
              <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px 16px" }}>
                <div>
                  <span style={{ color: "#838383", fontSize: 13, marginRight: 4 }}>{t("Weight")}:</span>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{Math.round(nutrition.totalWeight)} g</span>
                </div>
                <div>
                  <span style={{ color: "#838383", fontSize: 13, marginRight: 4 }}>{t("Energy")}:</span>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{Math.round(nutrition.totalEnergy)} kcal</span>
                </div>
                <div>
                  <span style={{ color: "#3498db", fontSize: 13, marginRight: 4 }}>P:</span>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>
                    {Math.round(nutrition.totalProteins)} g
                    <span style={{ color: "#3498db", marginLeft: 3, fontSize: 12 }}>({Math.round(nutrition.proteinPercentage)}%)</span>
                  </span>
                </div>
                <div>
                  <span style={{ color: "#f39c12", fontSize: 13, marginRight: 4 }}>F:</span>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>
                    {Math.round(nutrition.totalFats)} g
                    <span style={{ color: "#f39c12", marginLeft: 3, fontSize: 12 }}>({Math.round(nutrition.fatPercentage)}%)</span>
                  </span>
                </div>
                <div>
                  <span style={{ color: "#27ae60", fontSize: 13, marginRight: 4 }}>C:</span>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>
                    {Math.round(nutrition.totalCarbs)} g
                    <span style={{ color: "#27ae60", marginLeft: 3, fontSize: 12 }}>({Math.round(nutrition.carbPercentage)}%)</span>
                  </span>
                </div>
                <MacronutrientPieChart
                  proteinPercentage={nutrition.proteinPercentage}
                  fatPercentage={nutrition.fatPercentage}
                  carbPercentage={nutrition.carbPercentage}
                />
              </div>
            </div>

            {/* Dish groups */}
            {groups.length > 0 ? (
              groups.map((group) => {
                const { dish, index } = group.main;
                const isBase = dish.type === DishType.BASE;
                const hasProtein = group.subDishes.some((s) => s.dish.type === DishType.PROTEIN);
                const hasSauce = group.subDishes.some((s) => s.dish.type === DishType.SAUCE);

                return (
                  <div key={`${dish.id}-${index}`} style={{ marginBottom: 8, border: "1px solid #f0f0f0", borderRadius: 8, overflow: "hidden" }}>
                    {/* Main dish */}
                    <div style={{ padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#fff" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ width: 56, height: 56, borderRadius: 4, overflow: "hidden", flexShrink: 0, border: "1px solid #e8e8e8" }}>
                          <img
                            src={dish.img || "/images/photoForGoFit.jpg"}
                            alt={getDishName(dish)}
                            onError={(e) => { e.target.onerror = null; e.target.src = "/images/photoForGoFit.jpg"; }}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        </div>
                        <div>
                          <div style={{ marginBottom: 2 }}>
                            <Tag color={DISH_TYPE_COLORS[dish.type]} style={{ fontSize: 10, textTransform: "uppercase", borderRadius: 999, padding: "0 8px" }}>
                              {dish.type || DishType.REGULAR}
                            </Tag>
                          </div>
                          <Text strong>{getDishName(dish)}</Text>
                          <div style={{ marginTop: 2 }}>
                            <Space size={4}>
                              <Tag style={{ fontSize: 11, margin: 0 }}>{Math.round(Number(dish.weight) || 0)}g</Tag>
                              <Tag color="orange" style={{ fontSize: 11, margin: 0 }}>{Math.round(Number(dish.energy) || 0)} kcal</Tag>
                              <Tag style={{ fontSize: 11, margin: 0 }}>P:{Math.round(Number(dish.proteins) || 0)}</Tag>
                              <Tag style={{ fontSize: 11, margin: 0 }}>F:{Math.round(Number(dish.fats) || 0)}</Tag>
                              <Tag style={{ fontSize: 11, margin: 0 }}>C:{Math.round(Number(dish.carbohydrates) || Number(dish.carbs) || 0)}</Tag>
                            </Space>
                          </div>
                          {/* Add Protein/Sauce buttons for BASE dishes */}
                          {isBase && (!hasProtein || !hasSauce) && (
                            <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                              {!hasProtein && (
                                <Button
                                  size="small"
                                  type="dashed"
                                  icon={<PlusOutlined />}
                                  onClick={() => handleAddSubDish(dateStr, group, DishType.PROTEIN)}
                                  style={{ borderColor: "#fa8c16", color: "#fa8c16" }}
                                >
                                  {t("Add Protein")}
                                </Button>
                              )}
                              {!hasSauce && (
                                <Button
                                  size="small"
                                  type="dashed"
                                  icon={<PlusOutlined />}
                                  onClick={() => handleAddSubDish(dateStr, group, DishType.SAUCE)}
                                  style={{ borderColor: "#52c41a", color: "#52c41a" }}
                                >
                                  {t("Add Sauce")}
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <Button
                        type="text"
                        danger
                        icon={<CloseOutlined />}
                        onClick={() => handleRemoveGroup(dateStr, group)}
                        title={t("Remove")}
                      />
                    </div>

                    {/* Sub-dishes (protein/sauce) */}
                    {group.subDishes.map(({ dish: sub, index: subIdx }) => (
                      <div
                        key={`${sub.id}-${subIdx}`}
                        style={{
                          borderTop: "1px solid #f0f0f0",
                          padding: "8px 16px 8px 40px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          background: sub.type === DishType.PROTEIN ? "#fffbe6" : "#f6ffed",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ width: 44, height: 44, borderRadius: 4, overflow: "hidden", flexShrink: 0, border: "1px solid #e8e8e8" }}>
                            <img
                              src={sub.img || "/images/photoForGoFit.jpg"}
                              alt={getDishName(sub)}
                              onError={(e) => { e.target.onerror = null; e.target.src = "/images/photoForGoFit.jpg"; }}
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          </div>
                          <div>
                            <Space size={4}>
                              <Tag
                                color={DISH_TYPE_COLORS[sub.type]}
                                style={{ fontSize: 10, textTransform: "uppercase" }}
                              >
                                {sub.type}
                              </Tag>
                              <Text strong style={{ fontSize: 13 }}>{getDishName(sub)}</Text>
                            </Space>
                            <div style={{ marginTop: 2 }}>
                              <Space size={4}>
                                <Tag style={{ fontSize: 10, margin: 0 }}>{Math.round(Number(sub.energy) || 0)} kcal</Tag>
                                <Tag style={{ fontSize: 10, margin: 0 }}>P:{Math.round(Number(sub.proteins) || 0)}</Tag>
                                <Tag style={{ fontSize: 10, margin: 0 }}>F:{Math.round(Number(sub.fats) || 0)}</Tag>
                                <Tag style={{ fontSize: 10, margin: 0 }}>C:{Math.round(Number(sub.carbohydrates) || Number(sub.carbs) || 0)}</Tag>
                              </Space>
                            </div>
                          </div>
                        </div>
                        <Space>
                          <Button
                            size="small"
                            icon={<SwapOutlined />}
                            onClick={() => handleChangeSubDish(dateStr, subIdx, group, sub.type)}
                            title={t("Change")}
                          />
                          <Button
                            size="small"
                            danger
                            icon={<CloseOutlined />}
                            onClick={() => handleRemoveSubDish(dateStr, subIdx)}
                            title={t("Remove")}
                          />
                        </Space>
                      </div>
                    ))}
                  </div>
                );
              })
            ) : (
              <Empty
                description={t("No dishes added for this day")}
                style={{ margin: "16px 0" }}
              />
            )}

            {/* Add Dish button */}
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              block
              onClick={() => setAddDishModal({ visible: true, date: dateStr, typeFilter: null, insertAtIndex: null, isMainDish: true })}
              style={{ marginTop: 8 }}
            >
              {t("Add Dish")}
            </Button>
          </div>
        );
      })}

      {addDishModal.visible && (
        <AddCustomDishModal
          date={addDishModal.date}
          visible={addDishModal.visible}
          onClose={() => setAddDishModal({ visible: false, date: null, typeFilter: null, insertAtIndex: null, isMainDish: false })}
          typeFilter={addDishModal.typeFilter}
          insertAtIndex={addDishModal.insertAtIndex}
          isMainDish={addDishModal.isMainDish}
        />
      )}
    </>
  );
}

export default CustomMenuBuilder;
